/**
 * Everything the app talks to outside the browser, gathered in one place so screens never know
 * whether it is real or fake: the sign-in session, the cellar storage, the owner's `admin`
 * function client and the label-scan (`assist`) client.
 *
 * Two backends: this file's local one (fake sign-in, in-memory storage with the sample data when
 * VITE_SEED_DEMO=true, an in-memory people directory), used by `npm run dev`, previews and tests
 * with no AWS account (CLAUDE.md data safety rule 1); and the real one in `aws-backend.ts`
 * (T074a), chosen by `load-backend.ts` for the deployed build only.
 */
import { createAdminClient, type AdminClient } from '../admin/client';
import { createFakeAdminDirectory } from '../admin/fake-directory';
import { createAssistClient, type AssistClient } from '../assist/client';
import { fakeAssistTransport } from '../assist/fake-assist';
import { configuredOwnerEmail, createFakeSession, fakeIdFor, type Session } from '../auth/session';
import { withDebugHooks } from '../store/debug-storage-hooks';
import {
  createFakePhotoStorage,
  createFakeStorage,
  type ObjectStorage,
  type PhotoStorage,
} from '../store/fake-storage';
import { buildSeedObjects, SEED_MEMBER_EMAIL } from '../store/seed-data';

export interface Backend {
  session: Session;
  storage: ObjectStorage;
  /** The label photos, next to the cellar documents (owner request, 2026-10-11). */
  photos: PhotoStorage;
  admin: AdminClient;
  /** Label scan (User Story 8); absent when the deployed stack has no `assist` function. */
  assist?: AssistClient;
}

/** The in-memory backend. Everything resets on page reload. */
export function createLocalBackend(seedDemo = import.meta.env.VITE_SEED_DEMO === 'true'): Backend {
  const ownerEmail = configuredOwnerEmail();
  const people = createFakeAdminDirectory({
    ownerEmail,
    members: seedDemo ? [SEED_MEMBER_EMAIL] : [],
    idFor: fakeIdFor,
  });
  return {
    session: createFakeSession({ ownerEmail, canSignIn: people.canSignIn }),
    storage: withDebugHooks(
      createFakeStorage(seedDemo ? buildSeedObjects(ownerEmail, fakeIdFor) : {}),
    ),
    photos: createFakePhotoStorage(),
    admin: createAdminClient(people.transport, async () => 'fake-access-token'),
    assist: createAssistClient(fakeAssistTransport),
  };
}
